import { Slider } from "antd";
import { useEffect, useState, useRef } from "react";
import {
  ColoredSliderWrapper,
  CustomTooltip,
  MarkWrapper,
  StatusPopover,
  StyledSliderWrapper,
} from "./styled";

import { ReactComponent as OnHoldOrange } from "assets/svg/onHoldOrange.svg";
import { ReactComponent as CancelCircle } from "assets/svg/cancelCircle.svg";
import { statusOptions } from "../../scenes/ProjectPlanner/components/GoalSettings/data";
import { useDispatch } from "react-redux";

// @ts-ignore
import { updateManualStatus } from "modules/actions/PlanActions";
import { LoadingOutlined } from "@ant-design/icons";
import { errorToast, successToast } from "../../utils/toast";

export type StatusSliderProps = {
  label: string;
  value: number | undefined;
  status?: number;
  color: string;
  icon: React.ElementType | null;
};

type PropsType = {
  selectStatus: number;
  planProgress: number | undefined;
  id: number;
  projectPlanId: number;
  open: boolean;
  isStatusLoading: {
    type: string;
    loading: boolean;
  };
  setIsStatusLoading: (value: { type: string; loading: boolean }) => void;
  onClose: () => void;
  setIsProgressLoading?: (value: boolean) => void;
};

const Mark = ({ number }: { number?: number }) => {
  return (
    <MarkWrapper>
      <div />
      <span>{number}</span>
    </MarkWrapper>
  );
};

const marks = {
  0: <Mark number={0} />,
  1: <Mark />,
  2: <Mark number={2} />,
  3: <Mark />,
  4: <Mark number={4} />,
  5: <Mark />,
  6: <Mark number={6} />,
  7: <Mark />,
  8: <Mark number={8} />,
  9: <Mark />,
  10: <Mark number={10} />,
};

export default function StatusSlider({
  selectStatus,
  planProgress,
  id,
  projectPlanId,
  open,
  isStatusLoading,
  setIsStatusLoading,
  onClose,
  setIsProgressLoading,
}: PropsType) {
  const dispatch = useDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(planProgress || 0);
  const [left, setLeft] = useState(0);

  const [status, setStatus] = useState<StatusSliderProps>({
    label: "",
    value: 0,
    status: 0,
    color: "",
    icon: null,
  });

  const ON_HOLD_STATUS = statusOptions[2].value;
  const CANCELED_STATUS = statusOptions[4].value;

  const updateTooltipPosition = () => {
    if (!wrapperRef.current) return;

    const sliderTrack = wrapperRef.current.querySelector(".ant-slider-rail");
    const sliderHandle = wrapperRef.current.querySelector(".ant-slider-handle");

    if (sliderTrack && sliderHandle) {
      const handleRect = sliderHandle.getBoundingClientRect();
      const wrapperRect = wrapperRef.current.getBoundingClientRect();

      const handleCenter =
        handleRect.left + handleRect.width / 2 - wrapperRect.left;

      setLeft(handleCenter - 3);
    }
  };

  useEffect(() => {
    if (!open) return;

    const timeoutId = setTimeout(updateTooltipPosition, 200);

    window.addEventListener("resize", updateTooltipPosition);

    if (wrapperRef.current) {
      const observer = new MutationObserver(() => {
        setTimeout(updateTooltipPosition, 50);
      });

      observer.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });

      const sliderHandle = wrapperRef.current.querySelector(
        ".ant-slider-handle"
      ) as HTMLElement;
      if (sliderHandle) {
        const handleDragMove = () => {
          requestAnimationFrame(updateTooltipPosition);
        };

        sliderHandle.addEventListener("mousedown", () => {
          document.addEventListener("mousemove", handleDragMove);
          document.addEventListener(
            "mouseup",
            () => {
              document.removeEventListener("mousemove", handleDragMove);
            },
            { once: true }
          );
        });
      }

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("resize", updateTooltipPosition);
        observer.disconnect();
      };
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateTooltipPosition);
    };
  }, [value, open, status, planProgress]);

  const updateStatus = (value: number) => {
    if (value === 0) {
      setStatus(statusOptions[0]);
    }

    if (value > 0 && value <= 9) {
      setStatus(statusOptions[1]);
    }

    if (value === 10) {
      setStatus(statusOptions[3]);
    }

    setValue(value);

    requestAnimationFrame(updateTooltipPosition);
  };

  const handleStatusChange = (status: StatusSliderProps) => {
    setStatus(status);
  };

  const handleUpdateStatusClose = (value: number) => {
    onClose();

    setIsStatusLoading({
      type: "",
      loading: false,
    });

    if (value === 10) {
      successToast("Your status was completed successfully");
    } else {
      successToast("Your status was updated successfully");
    }
  };

  const handleUpdateStatus = async (
    value: number,
    newStatus?: number,
    isPlanProgress = true
  ) => {
    try {
      const isPlanProgressChanged = isPlanProgress
        ? value !== planProgress
        : true;

      if (isPlanProgressChanged) {
        setIsProgressLoading?.(true);

        setIsStatusLoading({
          type: "status",
          loading: true,
        });

        const payload = {
          manual_override: "True",
          ...(isPlanProgress && { plan_progress: value }),
          status: newStatus || status?.value,
        };
        await dispatch(
          updateManualStatus(id, payload, projectPlanId, () => {
            setIsProgressLoading?.(false);
            handleUpdateStatusClose(value);
          }
          )
        );
      }
    } catch (error) {
      console.error(error);
      setIsStatusLoading({
        type: "",
        loading: false,
      });
      errorToast("Failed to update status. Please try again.");
    }
  };

  useEffect(() => {
    if (selectStatus) {
      const status =
        statusOptions.find((o) => o.value === selectStatus) || statusOptions[0];

      setStatus(status);
      setValue(planProgress || 0);
    }
  }, [selectStatus, planProgress]);

  return (
    <StatusPopover>
      <h3 className="status_title">Update Progress & Status</h3>

      <h5 className="status_subtitle">
        Progress & Status
        {isStatusLoading.loading && (
          <LoadingOutlined style={{ color: "#32A6F9" }} spin />
        )}
      </h5>

      <StyledSliderWrapper
        ref={wrapperRef}
        onMouseMove={() => {
          if (open) requestAnimationFrame(updateTooltipPosition);
        }}
      >
        <CustomTooltip
          left={left}
          color={status.color}
          disabled={isStatusLoading.loading}
        >
          {status.icon && <status.icon />}
          {status.label}
        </CustomTooltip>

        <ColoredSliderWrapper color={status?.color || "#32A6F9"}>
          <Slider
            min={0}
            max={10}
            value={value}
            marks={marks}
            tooltip={{ open: false }}
            step={1}
            onChange={updateStatus}
            onAfterChange={handleUpdateStatus}
            dots={false}
            disabled={isStatusLoading.loading}
          />
        </ColoredSliderWrapper>
      </StyledSliderWrapper>

      <h5 className="status_subtitle">Special Status Flags</h5>

      <div className="status_buttons">
        <button
          type="button"
          disabled={
            status?.value === ON_HOLD_STATUS ||
            isStatusLoading.type === "onhold" ||
            isStatusLoading.loading
          }
          onClick={() => {
            handleStatusChange(statusOptions[2]);
            setIsStatusLoading({ type: "onhold", loading: true });
            handleUpdateStatus(value, ON_HOLD_STATUS, false);
          }}
          style={{
            backgroundColor:
              status?.value === ON_HOLD_STATUS ? "rgba(234, 179, 8, 0.14)" : "",
          }}
        >
          {isStatusLoading.type === "onhold" ? (
            <LoadingOutlined style={{ color: "#8f8f90" }} spin />
          ) : (
            <>
              <OnHoldOrange />
              On Hold
            </>
          )}
        </button>
        <button
          type="button"
          disabled={
            status?.value === CANCELED_STATUS ||
            isStatusLoading.type === "cancel" ||
            isStatusLoading.loading
          }
          onClick={() => {
            handleStatusChange(statusOptions[4]);
            setIsStatusLoading({ type: "cancel", loading: true });
            handleUpdateStatus(value, CANCELED_STATUS, false);
          }}
          style={{
            backgroundColor:
              status?.value === CANCELED_STATUS
                ? "rgba(239, 68, 68, 0.14)"
                : "",
          }}
        >
          {isStatusLoading.type === "cancel" ? (
            <LoadingOutlined style={{ color: "#8f8f90" }} spin />
          ) : (
            <>
              <CancelCircle />
              Canceled
            </>
          )}
        </button>
      </div>
    </StatusPopover>
  );
}
